import React, { useState, useMemo } from "react";
import rentalQuestions from "../data/rental_questions.json";
import SectionTitle from "../components/SectionTitle";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import rentalForm from "../assets/lottie-files/rentalForm.lottie";
import marketMap from "../assets/marketMap.png";
import { motion, Variants } from "framer-motion";
import styled, { css, keyframes } from "styled-components";

// Framer Motion bubble variants for continuous floating animation
const bubbleVariants: Variants = {
  animate1: {
    y: [0, -30, 0],
    x: [0, 20, 0],
    transition: {
      duration: 8,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
  animate2: {
    y: [0, 25, 0],
    x: [0, -15, 0],
    transition: {
      duration: 10,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
  animate3: {
    y: [0, -20, 0],
    x: [0, 10, 0],
    transition: {
      duration: 7,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
  animate4: {
    y: [0, 15, 0],
    x: [0, -10, 0],
    transition: {
      duration: 9,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

// Styled Components
const Container = styled.div`
  width: 90%;
  max-width: 100%;
  margin: 3rem auto;
  padding: 3.5rem 3rem;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    padding: 2.5rem 1.2rem;
    width: 95%;
  }
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    width: 98%;
    margin: 1rem auto;
  }
  @media (max-width: 600px) {
    padding: 1.5rem 0.8rem;
    border-radius: 16px;
    margin: 0.5rem auto;
    width: 100%;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  }
  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
    margin: 0;
    border-radius: 12px;
  }
`;

const BubbleBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: visible;
`;

const StyledSectionTitle = styled.h2`
  font-size: 1.65rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2.5rem;
  padding-bottom: 0.85rem;
  margin-top: 0;
  z-index: 2;
  position: sticky;
  top: 0;
  left: 0;
  padding-top: 1.5rem;
  letter-spacing: -0.03em;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, rgb(222, 110, 73), rgb(255, 140, 100));
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 2rem;
    padding-bottom: 0.7rem;
  }
  @media (max-width: 600px) {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.6rem;
  }
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1.2rem;
    padding-bottom: 0.5rem;
  }
`;

const FormLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
`;

const StepContentScroll = styled.div`
  padding-bottom: 2.5rem;
  position: relative;
  z-index: 1;
`;

// Animation for section header
const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedSectionTitle = styled(StyledSectionTitle)<{ animate: boolean }>`
  ${({ animate }) =>
    animate &&
    css`
      animation: ${fadeSlideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    `}
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  background: #f1f3fa;
  border-radius: 12px;
  margin-bottom: 2rem;
  height: 10px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
`;

const ProgressBarFill = styled.div<{ percent: number }>`
  width: ${({ percent }) => percent}%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgb(222, 110, 73) 0%,
    rgb(255, 140, 100) 50%,
    rgb(255, 160, 120) 100%
  );
  border-radius: 12px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const FormField = styled.div`
  margin-bottom: 2rem;
  animation: fadeInUp 0.5s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.7rem;
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1.1rem;
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 0.6rem;
  }
  @media (max-width: 600px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.4rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  background-color: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  font-family: inherit;
  cursor: pointer;

  &::placeholder {
    color: #adb5bd;
  }

  &:hover {
    border-color: #ced4da;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
    box-shadow: 0 0 0 4px rgba(222, 110, 73, 0.12),
      0 4px 16px rgba(222, 110, 73, 0.2);
    background-color: #ffffff;
    transform: translateY(-2px);
  }

  /* For date and time inputs, make the entire field clickable */
  &[type="date"],
  &[type="time"] {
    cursor: pointer;
    position: relative;

    /* Hide the default calendar/time icons */
    &::-webkit-calendar-picker-indicator {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: auto;
      color: transparent;
      background: transparent;
      cursor: pointer;
    }

    /* For Firefox */
    &::-moz-calendar-picker-indicator {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: auto;
      color: transparent;
      background: transparent;
      cursor: pointer;
    }
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    border-radius: 12px;
  }
  @media (max-width: 600px) {
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
    border-radius: 10px;
  }
  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    border-radius: 8px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 2.5rem;
`;

const DropdownHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  background-color: #ffffff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;

  &:hover {
    border-color: #ced4da;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      border-color: #5c7cfa;
      box-shadow: 0 0 0 4px rgba(92, 124, 250, 0.12),
        0 4px 16px rgba(92, 124, 250, 0.2);
    `}
`;

const DropdownList = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  padding: 0.5rem 0;
  margin: 4px 0 0 0;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  max-height: ${({ isOpen }) => (isOpen ? "200px" : "0")};
  overflow-y: auto;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: max-height 0.3s ease, opacity 0.3s ease;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  position: absolute;
  width: 100%;
  z-index: 5;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e9ecef;
    border-radius: 8px;
  }
`;

const DropdownOption = styled.li`
  padding: 0.7rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #f0f4ff;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  background-color: #ffffff;
  resize: vertical;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  line-height: 1.6;
  font-family: inherit;

  &::placeholder {
    color: #adb5bd;
  }

  &:hover {
    border-color: #ced4da;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
    box-shadow: 0 0 0 4px rgba(222, 110, 73, 0.12),
      0 4px 16px rgba(222, 110, 73, 0.2);
    background-color: #ffffff;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    min-height: 100px;
    border-radius: 12px;
  }
  @media (max-width: 600px) {
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
    min-height: 90px;
    border-radius: 10px;
  }
  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    min-height: 80px;
    border-radius: 8px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  padding: 1.8rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

  &:hover {
    border-color: #ced4da;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 14px;
  }
  @media (max-width: 600px) {
    gap: 0.8rem;
    padding: 1.2rem;
    border-radius: 12px;
  }
  @media (max-width: 480px) {
    gap: 0.6rem;
    padding: 1rem;
    border-radius: 10px;
    flex-direction: column;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2a3a8c;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.7rem 1rem;
  background: white;
  border-radius: 10px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

  &:hover {
    background: #f8f9fa;
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(222, 110, 73, 0.1);
  }

  input {
    margin-right: 0.85rem;
    width: 24px;
    height: 24px;
    cursor: pointer;
    accent-color: rgb(222, 110, 73);
    transition: transform 0.2s ease;
  }

  input:checked {
    transform: scale(1.1);
  }

  &:has(input:checked) {
    border-color: rgb(222, 110, 73);
    background: linear-gradient(
      135deg,
      rgba(222, 110, 73, 0.1) 0%,
      #ffffff 100%
    );
    box-shadow: 0 4px 12px rgba(222, 110, 73, 0.15);
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.6rem 0.9rem;
    border-radius: 8px;
  }
  @media (max-width: 600px) {
    font-size: 0.9rem;
    padding: 0.5rem 0.8rem;
    border-radius: 6px;
  }
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.4rem 0.7rem;
    border-radius: 5px;
    width: 100%;
    justify-content: flex-start;
  }
`;

const Button = styled.button<{ secondary?: boolean }>`
  background: ${({ secondary }) =>
    secondary
      ? "linear-gradient(135deg, #dee2e6, #f1f3f5)"
      : "linear-gradient(135deg, rgb(222, 110, 73), rgb(200, 90, 60))"};
  color: ${({ secondary }) => (secondary ? "#1a1a1a" : "white")};
  padding: 0.75rem 1.8rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  margin-right: 1rem;
  box-shadow: 0 6px 15px rgba(222, 110, 73, 0.18);
  transition: background 0.4s ease, box-shadow 0.4s ease, transform 0.2s ease,
    color 0.2s;

  &:hover {
    background: ${({ secondary }) =>
      secondary
        ? "linear-gradient(135deg, #f1f3f5, #dee2e6)"
        : "linear-gradient(135deg, rgb(200, 90, 60), rgb(222, 110, 73))"};
    box-shadow: 0 8px 20px rgba(222, 110, 73, 0.12);
    color: ${({ secondary }) => (secondary ? "#1a1a1a" : "#fff")};
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.18);
  }

  @media (max-width: 768px) {
    padding: 0.7rem 1.5rem;
    font-size: 1.05rem;
    border-radius: 12px;
    margin-right: 0.8rem;
  }
  @media (max-width: 600px) {
    padding: 0.6rem 1.2rem;
    font-size: 1rem;
    border-radius: 10px;
    margin-right: 0.6rem;
  }
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.95rem;
    border-radius: 8px;
    margin-right: 0.5rem;
    width: 100%;
    margin-bottom: 0.5rem;
  }
`;

const FieldNote = styled.p`
  font-size: 0.9rem;
  color: rgb(0, 0, 0);
  margin-top: 0.4rem;
  margin-bottom: 3;
  line-height: 1.4;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-left: 3px solid rgb(222, 110, 73);
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
`;

const MarketMapImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 1.5rem 0;
  border: 2px solid rgba(222, 110, 73, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(222, 110, 73, 0.15);
  }
`;

const ImageModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  cursor: default;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(222, 110, 73, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(222, 110, 73, 1);
  }
`;

const SummaryContainer = styled.div<{ isVisible: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  padding: 2.5rem;
  margin: 2rem 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(222, 110, 73, 0.2);
  transform: ${({ isVisible }) =>
    isVisible ? "translateY(0)" : "translateY(20px)"};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, rgb(222, 110, 73), rgb(255, 140, 100));
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1.5rem 0;
    border-radius: 16px;
  }
  @media (max-width: 600px) {
    padding: 1.5rem;
    margin: 1rem 0;
    border-radius: 14px;
  }
  @media (max-width: 480px) {
    padding: 1rem;
    margin: 0.5rem 0;
    border-radius: 12px;
  }
`;

const SummaryTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, rgb(222, 110, 73), rgb(255, 140, 100));
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 1.2rem;
  }
  @media (max-width: 600px) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
  }
`;

const SummarySection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(222, 110, 73, 0.05);
  border-radius: 12px;
  border-left: 4px solid rgb(222, 110, 73);
`;

const SummarySectionTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "📋";
    font-size: 1.1rem;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(222, 110, 73, 0.1);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.6rem 0;
  }
  @media (max-width: 480px) {
    padding: 0.5rem 0;
  }
`;

const SummaryLabel = styled.span`
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
  margin-right: 1rem;

  @media (max-width: 600px) {
    margin-right: 0;
    font-size: 0.95rem;
  }
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const SummaryValue = styled.span`
  color: #666;
  flex: 2;
  text-align: right;
  word-break: break-word;

  @media (max-width: 600px) {
    text-align: left;
    font-size: 0.95rem;
  }
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const SummaryActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid rgba(222, 110, 73, 0.1);

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
  }
  @media (max-width: 600px) {
    margin-top: 1.2rem;
    padding-top: 1.2rem;
    gap: 0.8rem;
  }
  @media (max-width: 480px) {
    margin-top: 1rem;
    padding-top: 1rem;
    gap: 0.5rem;
    flex-direction: column;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(
    to bottom right,
    rgb(222, 110, 73) 65%,
    rgb(240, 240, 240) 100%
  );
  padding: 4rem 1rem;
  color: white;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
  @media (max-width: 600px) {
    padding: 2.5rem 0.8rem;
  }
  @media (max-width: 480px) {
    padding: 2rem 0.5rem;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }
  @media (max-width: 600px) {
    gap: 1rem;
  }
  @media (max-width: 480px) {
    gap: 0.8rem;
  }
`;

const HeroText = styled.div`
  flex: 1 1 500px;
  min-width: 300px;

  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: auto;
    width: 100%;
  }
`;

const HeroAnimation = styled.div`
  flex: 1 1 400px;
  min-width: 300px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: auto;
    width: 100%;
    max-width: 400px;
  }
  @media (max-width: 600px) {
    max-width: 350px;
  }
  @media (max-width: 480px) {
    max-width: 300px;
  }
`;

// --- Dynamic Form State based on rental_questions.json ---
// We'll infer the form shape from the JSON, including support for multiselect and conditional fields.
type FormData = {
  [key: string]: any;
};

// Build initialFormData from rentalQuestions
function buildInitialFormData(questions: any[]): FormData {
  const data: FormData = {};
  for (const q of questions) {
    if (q.type === "checkbox") {
      data[q.id] = false;
    } else if (q.type === "multiselect") {
      data[q.id] = [];
      // For "other" option, support a details field
      if (q.options && q.options.some((opt: any) => opt.value === "Other")) {
        data[`${q.id}OtherDetails`] = "";
      }
    } else if (q.type === "number") {
      data[q.id] = q.default !== undefined ? q.default : "";
    } else {
      data[q.id] = "";
    }
    // Support for conditional fields (e.g., kitchenHours as a pair)
    if (q.conditional && q.conditional.addFields) {
      for (const addField of q.conditional.addFields) {
        data[addField.id] = "";
      }
    }
  }
  // Special handling for anticipatedAttendance - add the number field
  data["anticipatedAttendanceNumber"] = "";
  // Always include notes for summary page (if not already present)
  if (!("notes" in data)) {
    data["notes"] = "";
  }
  return data;
}

function getVisibleQuestions(formData: FormData) {
  return rentalQuestions.filter((q) => {
    if (!q.conditional) return true;
    const cond = q.conditional as any;
    const value = (formData as any)[cond.field];
    // Handle "includes" for multiselect conditionals
    if (cond.includes !== undefined) {
      // value should be an array
      return Array.isArray(value) && value.includes(cond.includes);
    }
    // Handle "equals" for simple equality
    if (cond.equals !== undefined) {
      return value === cond.equals;
    }
    // Handle "in" for value in array
    if (cond.in !== undefined) {
      return cond.in.includes(value);
    }
    return true;
  });
}

function getSectionForQuestion(index: number, visibleQuestions: any[]) {
  // Find the section title for the current question index
  return visibleQuestions[index]?.section || "";
}

export default function RentalQuestionnaire() {
  // Build initialFormData once from rentalQuestions
  const initialFormData = useMemo(
    () => buildInitialFormData(rentalQuestions),
    []
  );
  const [formData, setFormData] = useState<FormData>({ ...initialFormData });
  const [submitting, setSubmitting] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Memoize visible questions based on formData for conditional logic
  const visibleQuestions = useMemo(
    () => getVisibleQuestions(formData),
    [formData]
  );

  // Form change handler (handles all types, including multiselect, textarea, select)
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, type } = e.target;
    // For checkboxes and text inputs, use type assertions for value/checked
    if (
      type === "checkbox" &&
      (e.target as HTMLInputElement).dataset.multiselect === "true"
    ) {
      // For multiselect, name is the field, value is the option
      const value = (e.target as HTMLInputElement).value;
      const checked = (e.target as HTMLInputElement).checked;
      const arr = Array.isArray(formData[name]) ? [...formData[name]] : [];
      if (checked) {
        if (!arr.includes(value)) arr.push(value);
      } else {
        const idx = arr.indexOf(value);
        if (idx > -1) arr.splice(idx, 1);
      }
      setFormData({ ...formData, [name]: arr });
    } else if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else if (type === "number") {
      const value = e.target.value;
      setFormData({
        ...formData,
        [name]: value === "" ? "" : Number(value),
      });
    } else {
      const value = e.target.value;
      setFormData({ ...formData, [name]: value });
    }
  };

  // Generate summary data
  const generateSummaryData = () => {
    const summary: { [key: string]: any } = {};

    visibleQuestions.forEach((q) => {
      const value = formData[q.id];
      if (value !== undefined && value !== null && value !== "") {
        if (q.type === "multiselect" && Array.isArray(value)) {
          if (value.length > 0) {
            summary[q.label] = value
              .map((v: string) => {
                const option = q.options?.find((opt: any) => opt.value === v);
                return option ? option.label : v;
              })
              .join(", ");
          }
        } else if (q.type === "checkbox") {
          if (value) {
            summary[q.label] = "Yes";
          }
        } else if (q.id === "anticipatedAttendance") {
          if (value === "openToPublic") {
            summary[q.label] = "Open to the Public";
          } else if (formData["anticipatedAttendanceNumber"]) {
            summary[q.label] = formData["anticipatedAttendanceNumber"];
          }
        } else if (q.type === "time") {
          // Format time to show AM/PM
          if (value) {
            const [hours, minutes] = value.split(":");
            const hour24 = parseInt(hours);
            const hour12 =
              hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
            const ampm = hour24 >= 12 ? "PM" : "AM";
            summary[q.label] = `${hour12}:${minutes} ${ampm}`;
          }
        } else {
          summary[q.label] = value;
        }
      }
    });

    return summary;
  };

  // Submit handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      alert("Form submitted! (backend integration coming next)");
    }, 1200);
  };

  // Validation for required questions (all types, including multiselect)
  function isQuestionAnswered(question: any): boolean {
    if (!question) return true;
    if (!question.required) return true;

    // Special handling for anticipatedAttendance
    if (question.id === "anticipatedAttendance") {
      const isOpenToPublic =
        formData["anticipatedAttendance"] === "openToPublic";
      const attendanceNumber = formData["anticipatedAttendanceNumber"];
      return isOpenToPublic || (attendanceNumber && attendanceNumber !== "");
    }

    const v = formData[question.id];
    if (question.type === "multiselect") {
      return Array.isArray(v) && v.length > 0;
    }
    if (question.type === "select") {
      return typeof v === "string" && v.trim() !== "";
    }
    if (
      question.type === "textarea" ||
      question.type === "text" ||
      question.type === "email" ||
      question.type === "tel"
    ) {
      return typeof v === "string" && v.trim() !== "";
    }
    if (question.type === "number") {
      return v !== undefined && v !== null && v !== "";
    }
    if (question.type === "checkbox") {
      // Considered always valid (not required to be checked)
      return true;
    }
    return !!v;
  }

  // Render a single question field (handles all types, including multiselect and conditional fields)
  function renderQuestionField(q: any) {
    // Special handling for anticipatedAttendance: render as mutually exclusive options
    if (q.id === "anticipatedAttendance") {
      const isOpenToPublic =
        formData["anticipatedAttendance"] === "openToPublic";
      const attendanceNumber = formData["anticipatedAttendanceNumber"] || "";

      return (
        <FormField>
          <Label>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}

          {/* Open to Public Checkbox */}
          <CheckboxGroup style={{ marginBottom: "1rem" }}>
            <CheckboxLabel htmlFor="openToPublic">
              <Input
                id="openToPublic"
                type="checkbox"
                name="anticipatedAttendance"
                value="openToPublic"
                checked={isOpenToPublic}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      anticipatedAttendance: "openToPublic",
                      anticipatedAttendanceNumber: "",
                    });
                  } else {
                    setFormData({
                      ...formData,
                      anticipatedAttendance: "",
                      anticipatedAttendanceNumber: "",
                    });
                  }
                }}
              />
              Open to the Public
            </CheckboxLabel>
          </CheckboxGroup>

          {/* Number Input - only show if not "Open to Public" */}
          {!isOpenToPublic && (
            <div style={{ marginTop: "1rem" }}>
              <Label htmlFor="anticipatedAttendanceNumber">
                Expected Number of Attendees
              </Label>
              <Input
                id="anticipatedAttendanceNumber"
                type="number"
                name="anticipatedAttendanceNumber"
                value={attendanceNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    anticipatedAttendanceNumber: value,
                    anticipatedAttendance: value
                      ? ""
                      : formData.anticipatedAttendance,
                  });
                }}
                placeholder="Enter expected number of attendees"
                min="1"
              />
            </div>
          )}
        </FormField>
      );
    }

    // Special handling for rentalSpaceRequested: render as a multiselect checkbox group
    if (q.id === "rentalSpaceRequested") {
      // We'll render as a multiselect checkbox group
      // Ensure formData["rentalSpaceRequested"] is an array
      const values = Array.isArray(formData["rentalSpaceRequested"])
        ? formData["rentalSpaceRequested"]
        : [];
      return (
        <FormField>
          <Label>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}

          {/* Market Map Image for Reference */}
          <div style={{ textAlign: "center", margin: "1.5rem 0" }}>
            <MarketMapImage
              src={marketMap}
              alt="Market Layout Map - Reference for rental spaces"
              title="Click to view full size"
              onClick={() => setIsImageModalOpen(true)}
            />
            <p
              style={{
                fontSize: "0.85rem",
                color: "#666",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Market layout for reference - click image to view full size
            </p>
          </div>

          <CheckboxGroup>
            {q.options.map((opt: any) => (
              <CheckboxLabel
                key={opt.value}
                htmlFor={`rentalSpaceRequested_${opt.value}`}
              >
                <Input
                  id={`rentalSpaceRequested_${opt.value}`}
                  type="checkbox"
                  name="rentalSpaceRequested"
                  value={opt.value}
                  data-multiselect="true"
                  checked={values.includes(opt.value)}
                  onChange={handleChange}
                />
                {opt.label}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>

          {/* If "Other" is selected, show a conditional text input */}
          {q.options.some((opt: any) => opt.value === "Other") &&
            values.includes("Other") && (
              <FormField style={{ marginTop: "1.2rem" }}>
                <Label htmlFor={`rentalSpaceRequestedOtherDetails`}>
                  Please specify other:
                </Label>
                <Input
                  id={`rentalSpaceRequestedOtherDetails`}
                  type="text"
                  name={`rentalSpaceRequestedOtherDetails`}
                  value={formData[`rentalSpaceRequestedOtherDetails`] || ""}
                  onChange={handleChange}
                  placeholder="Enter details"
                />
              </FormField>
            )}
        </FormField>
      );
    }
    // Multiselect (checkbox group)
    if (q.type === "multiselect") {
      const values = formData[q.id] || [];
      return (
        <FormField>
          <Label>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <CheckboxGroup>
            {q.options.map((opt: any) => (
              <CheckboxLabel key={opt.value} htmlFor={`${q.id}_${opt.value}`}>
                <Input
                  id={`${q.id}_${opt.value}`}
                  type="checkbox"
                  name={q.id}
                  value={opt.value}
                  data-multiselect="true"
                  checked={values.includes(opt.value)}
                  onChange={handleChange}
                />
                {opt.label}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>
          {/* If "Other" is selected, show a conditional text input */}
          {q.options.some((opt: any) => opt.value === "Other") &&
            values.includes("Other") && (
              <FormField style={{ marginTop: "1.2rem" }}>
                <Label htmlFor={`${q.id}OtherDetails`}>
                  Please specify other:
                </Label>
                <Input
                  id={`${q.id}OtherDetails`}
                  type="text"
                  name={`${q.id}OtherDetails`}
                  value={formData[`${q.id}OtherDetails`] || ""}
                  onChange={handleChange}
                  placeholder="Enter details"
                />
              </FormField>
            )}
        </FormField>
      );
    }
    // Checkbox is special: show as a single checkbox in the center
    if (q.type === "checkbox") {
      return (
        <CheckboxGroup>
          <CheckboxLabel htmlFor={q.id}>
            <Label
              htmlFor={q.id}
              style={{
                marginBottom: 0,
                marginRight: "0.5rem",
                fontWeight: 600,
                fontSize: "1.15rem",
                color: "#2a3a8c",
                display: "none",
              }}
            >
              {q.label}
            </Label>
            {q.note && <FieldNote>{q.note}</FieldNote>}
            <Input
              id={q.id}
              type="checkbox"
              name={q.id}
              checked={!!formData[q.id]}
              onChange={handleChange}
            />
            {q.label}
          </CheckboxLabel>
        </CheckboxGroup>
      );
    }
    // Textarea
    if (q.type === "textarea") {
      return (
        <FormField>
          <Label htmlFor={q.id}>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <Textarea
            id={q.id}
            name={q.id}
            value={formData[q.id]}
            onChange={handleChange}
            placeholder={q.placeholder || ""}
          />
        </FormField>
      );
    }
    // Select dropdown
    if (q.type === "select") {
      const isOpen = openDropdownId === q.id;
      const value = formData[q.id];
      const handleOptionClick = (val: string) => {
        setFormData({ ...formData, [q.id]: val });
        setOpenDropdownId(null);
      };
      return (
        <FormField>
          <Label htmlFor={q.id}>{q.label}</Label>
          <DropdownContainer>
            <DropdownHeader
              onClick={() => setOpenDropdownId(isOpen ? null : q.id)}
              isOpen={isOpen}
            >
              {value
                ? q.options.find((opt: any) => opt.value === value)?.label
                : "Select an option"}
              <span
                style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            </DropdownHeader>
            <DropdownList isOpen={isOpen}>
              {q.options.map((opt: any) => (
                <DropdownOption
                  key={opt.value}
                  onClick={() => handleOptionClick(opt.value)}
                >
                  {opt.label}
                </DropdownOption>
              ))}
            </DropdownList>
          </DropdownContainer>
          {q.note && <FieldNote>{q.note}</FieldNote>}
        </FormField>
      );
    }
    // Conditional fields (e.g., kitchenHours for kitchen rental)
    // (handled inline in rentalSpaceRequested multiselect above)
    // Conditional text input for "Other" in multiselect handled above
    // Number, text, email, tel, date, time
    return (
      <FormField>
        <Label htmlFor={q.id}>{q.label}</Label>
        {q.note && <FieldNote>{q.note}</FieldNote>}
        <Input
          id={q.id}
          type={q.type}
          name={q.id}
          value={formData[q.id] ?? ""}
          min={q.min}
          onChange={handleChange}
          placeholder={q.placeholder || ""}
          required={q.required}
          onClick={(e) => {
            // For date and time inputs, ensure the picker opens on any click
            if (q.type === "date" || q.type === "time") {
              e.currentTarget.showPicker?.();
            }
          }}
        />
      </FormField>
    );
  }

  // All questions are always visible (no progressive reveal)
  type VisibleQuestion = {
    question: any;
    index: number;
    section: string;
    showSectionHeader: boolean;
  };
  const verticalVisibleQuestions: VisibleQuestion[] = visibleQuestions.map(
    (q, i) => {
      const section = getSectionForQuestion(i, visibleQuestions);
      // show section header if section is non-empty and different from previous
      const showSectionHeader =
        i === 0 || section !== getSectionForQuestion(i - 1, visibleQuestions);
      return {
        question: q,
        index: i,
        section,
        showSectionHeader,
      };
    }
  );
  // Progress percent: number of required questions answered / total required questions
  const requiredQuestions = visibleQuestions.filter((q) => q.required);
  const numRequiredAnswered =
    requiredQuestions.filter(isQuestionAnswered).length;
  const progressPercent =
    requiredQuestions.length > 0
      ? Math.round((numRequiredAnswered / requiredQuestions.length) * 100)
      : 100;
  // Section color themes for bubbles (use first section of visible questions)
  const sectionColors = {
    "Client Contact Information": {
      primary: "rgb(222, 110, 73)",
      secondary: "rgb(255, 140, 100)",
    },
    "Event Details": {
      primary: "rgb(200, 90, 60)",
      secondary: "rgb(255, 160, 120)",
    },
    "Additional Services": {
      primary: "rgb(180, 70, 40)",
      secondary: "rgb(255, 180, 140)",
    },
    "Notes & Summary": {
      primary: "rgb(160, 50, 20)",
      secondary: "rgb(255, 200, 160)",
    },
  };
  const firstSection =
    verticalVisibleQuestions.length > 0
      ? verticalVisibleQuestions[0].section
      : "Client Contact Information";
  const currentColors =
    sectionColors[firstSection as keyof typeof sectionColors] ||
    sectionColors["Client Contact Information"];

  return (
    <div>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroText>
            <SectionTitle
              parts={[{ text: "🏢 SJFM Rental Questionnaire", color: "white" }]}
              align="left"
            />
            <p style={{ fontSize: "1.25rem", lineHeight: "1.6" }}>
              Thank you for your interest in renting space at the St. John's
              Farmers' Market. Please provide your event details, and we'll
              review your request, calculate the cost, and send a contract for
              your approval.
            </p>
          </HeroText>
          <HeroAnimation>
            <DotLottieReact
              src={rentalForm}
              loop
              autoplay
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </HeroAnimation>
        </HeroContent>
      </HeroSection>
      {/* Main Container */}
      <Container>
        {/* Decorative floating bubbles background */}
        <BubbleBackground>
          <motion.div
            style={{
              position: "absolute",
              top: "10%",
              left: "7%",
              width: 90,
              height: 90,
              zIndex: 0,
              opacity: 0.17,
              filter: "blur(0.5px)",
              background: `radial-gradient(circle at 60% 40%, ${currentColors.primary} 80%, ${currentColors.secondary} 100%)`,
              borderRadius: "50%",
            }}
            variants={bubbleVariants}
            animate="animate1"
          />
          <motion.div
            style={{
              position: "absolute",
              top: "65%",
              left: "80%",
              width: 60,
              height: 60,
              zIndex: 0,
              opacity: 0.13,
              background: `radial-gradient(circle at 40% 60%, ${currentColors.primary} 80%, ${currentColors.secondary} 100%)`,
              borderRadius: "50%",
            }}
            variants={bubbleVariants}
            animate="animate2"
          />
          <motion.div
            style={{
              position: "absolute",
              top: "80%",
              left: "5%",
              width: 45,
              height: 45,
              zIndex: 0,
              opacity: 0.11,
              background: `radial-gradient(circle at 60% 60%, ${currentColors.primary} 80%, ${currentColors.secondary} 100%)`,
              borderRadius: "50%",
            }}
            variants={bubbleVariants}
            animate="animate3"
          />
          <motion.div
            style={{
              position: "absolute",
              top: "25%",
              left: "70%",
              width: 75,
              height: 75,
              zIndex: 0,
              opacity: 0.15,
              background: `radial-gradient(circle at 50% 50%, ${currentColors.primary} 80%, ${currentColors.secondary} 100%)`,
              borderRadius: "50%",
            }}
            variants={bubbleVariants}
            animate="animate4"
          />
        </BubbleBackground>
        {/* <ProgressBarContainer>
          <ProgressBarFill percent={progressPercent} />
        </ProgressBarContainer> */}
        <form
          id="rental-form"
          onSubmit={handleSubmit}
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <FormLayout>
            <StepContentScroll>
              {/* Render all questions vertically, all always visible */}
              {verticalVisibleQuestions.map(
                ({ question: q, section, showSectionHeader }) => (
                  <React.Fragment key={q.id}>
                    {showSectionHeader && (
                      <AnimatedSectionTitle
                        animate={true}
                        style={{ marginBottom: "2.5rem" }}
                      >
                        {section}
                      </AnimatedSectionTitle>
                    )}
                    {renderQuestionField(q)}
                  </React.Fragment>
                )
              )}
            </StepContentScroll>
          </FormLayout>
        </form>

        {/* Summary Section - Always visible at the end */}
        <SummaryContainer isVisible={true}>
          <SummaryTitle>📋 Form Summary</SummaryTitle>

          {Object.entries(generateSummaryData()).map(
            ([label, value], index) => (
              <SummaryItem key={index}>
                <SummaryLabel>{label}:</SummaryLabel>
                <SummaryValue>{String(value)}</SummaryValue>
              </SummaryItem>
            )
          )}

          {Object.keys(generateSummaryData()).length === 0 && (
            <div
              style={{
                textAlign: "center",
                color: "#666",
                fontStyle: "italic",
                padding: "2rem",
              }}
            >
              No information entered yet. Please fill out the form above.
            </div>
          )}

          {/* Submit Button integrated in summary */}
          <SummaryActions>
            <Button type="submit" disabled={submitting} form="rental-form">
              {submitting ? "Submitting..." : "🚀 Submit Application"}
            </Button>
          </SummaryActions>
        </SummaryContainer>
      </Container>

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClick={() => setIsImageModalOpen(false)}
      >
        <CloseButton
          onClick={(e) => {
            e.stopPropagation();
            setIsImageModalOpen(false);
          }}
        >
          ×
        </CloseButton>
        <ModalImage
          src={marketMap}
          alt="Market Layout Map - Full Size"
          onClick={(e) => e.stopPropagation()}
        />
      </ImageModal>
    </div>
  );
}
